import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import ReactSelectCreatable from 'react-select/creatable'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { TClusterStatus } from '~/types/cluster'

import Button from '~/components/Buttons/Button'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextArea from '~/components/Inputs/ReactHookForm/TextArea'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Label from '~/components/Label'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { CLUSTER_STATUSES } from '~/const/clusters'
import { keys as clustersKeys, useDeleteClusterMutation, useUpdateClusterMutation } from '~/queries/clusters'
import { useUsersListQuery } from '~/queries/users'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { capitalize } from '~/utils/string'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const MODAL_ID = 'edit-cluster-modal'

interface FormValues {
  description?: string
  enableKeywordScrapper: boolean
  enableReviewsScrapper: boolean
  highVolumeThreshold: number
  name: string
  nbLeaders: number
  status: TClusterStatus
  tags: string[]
  userId?: string
}

const statusOptions = CLUSTER_STATUSES.map(({ label, value }) => ({ label, value }))
const statusValues = CLUSTER_STATUSES.map(({ value }) => value)

const validationSchema = Yup.object().shape({
  description: Yup.string(),
  enableKeywordScrapper: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  enableReviewsScrapper: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  highVolumeThreshold: Yup.number()
    .typeError(FORM_ERRORS.NUMBER)
    .required(FORM_ERRORS.REQUIRED)
    .min(0, FORM_ERRORS.MIN_VALUE(0)),
  name: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  nbLeaders: Yup.number().typeError(FORM_ERRORS.NUMBER).required(FORM_ERRORS.REQUIRED).min(0, FORM_ERRORS.MIN_VALUE(0)),
  status: Yup.string().oneOf(statusValues).required(FORM_ERRORS.REQUIRED),
  tags: Yup.array().of(Yup.string().required(FORM_ERRORS.REQUIRED)).required(),
  userId: Yup.string(),
})

interface Props {
  clusterId?: string
  clusterTags?: string[]
  editClusterParams: {
    description: string | null
    highVolumeThreshold: number | null
    isGetKeywordsEnabled: boolean | null
    isGetReviewsEnabled: boolean | null
    name: string | null
    nbLeaders: number | null
    status: TClusterStatus | null
    tags: string[]
    userId?: string | null
  }
  isOpen: boolean
  onClose: VoidFunction
}

const EditClusterModal = ({ clusterId, clusterTags = [], editClusterParams, isOpen, onClose }: Props): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const {
    description,
    highVolumeThreshold,
    isGetKeywordsEnabled,
    isGetReviewsEnabled,
    name,
    nbLeaders,
    status,
    tags,
    userId,
  } = editClusterParams

  const defaultValues: FormValues = {
    description: '',
    enableKeywordScrapper: isGetKeywordsEnabled || false,
    enableReviewsScrapper: isGetReviewsEnabled || false,
    highVolumeThreshold: highVolumeThreshold || 0,
    name: name || '',
    nbLeaders: nbLeaders || 0,
    status: status || 'todo',
    tags: [],
    userId: userId || '',
  }

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
    setValue,
  } = methods

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)

  const options = clusterTags
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map((el) => ({ label: el, value: el }))

  /** Queries & Mutations */

  const { mutateAsync: updateCluster } = useUpdateClusterMutation()

  const { isPending, mutateAsync: deleteCluster } = useDeleteClusterMutation()

  const { data: users } = useUsersListQuery()

  /** Handlers */

  const userOptions =
    users
      ?.filter(({ name }) => name !== '')
      .map(({ name, user_id }) => ({
        label: capitalize(name),
        value: user_id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || []

  const handleModalClose = () => {
    reset(defaultValues)
    onClose()
  }

  const handleFormSubmit = async (values: FormValues) => {
    if (clusterId) {
      try {
        await updateCluster({
          clusterId,
          description: values.description || '',
          highVolumeThreshold: values.highVolumeThreshold,
          isGetKeywordsEnabled: values.enableKeywordScrapper,
          isGetReviewsEnabled: values.enableReviewsScrapper,
          name: values.name,
          nbLeaders: values.nbLeaders,
          status: values.status,
          tags: values.tags,
          userId: values.userId || '',
        })
        await queryClient.invalidateQueries({ queryKey: clustersKeys.details(clusterId) })
        await queryClient.invalidateQueries({ queryKey: clustersKeys.clusterStats(clusterId) })
        displayToast('Cluster successfully updated', 'success')
        handleModalClose()
      } catch (e) {
        captureException(e)
        displayToast('Cluster could not be updated', 'error')
      }
    }
  }

  const handleDeleteConfirm = async () => {
    if (clusterId) {
      try {
        await deleteCluster({ clusterId })
        await queryClient.invalidateQueries({ queryKey: clustersKeys.listClusters({}) })
        displayToast('Cluster successfully deleted', 'success')
        navigate('/competitors/clusters', { replace: true })
      } catch (e) {
        captureException(e)
        displayToast('Cluster could not be deleted', 'error')
      }
    }
  }

  const handleSelectFieldChange = useCallback(
    (field: keyof FormValues, selectedOption: IOption | IOption[] | null) => {
      setValue(
        field,
        Array.isArray(selectedOption)
          ? selectedOption.map(({ value }) => value)
          : selectedOption
            ? selectedOption.value
            : '',
        {
          shouldDirty: true,
          shouldTouch: true,
        }
      )
    },
    [setValue]
  )

  const handleDeleteModalState = (isOpen: boolean) => () => setIsConfirmModalOpen(isOpen)

  /** Effects */

  useEffect(() => {
    if (isOpen) {
      reset({
        description: description || '',
        enableKeywordScrapper: isGetKeywordsEnabled || false,
        enableReviewsScrapper: isGetReviewsEnabled || false,
        highVolumeThreshold: highVolumeThreshold || 0,
        name: name || '',
        nbLeaders: nbLeaders || 0,
        status: status || 'todo',
        tags: tags || [],
        userId: userId || '',
      })
    }
  }, [
    description,
    highVolumeThreshold,
    isGetKeywordsEnabled,
    isGetReviewsEnabled,
    isOpen,
    name,
    nbLeaders,
    reset,
    status,
    tags,
    userId,
  ])

  /** Render */

  return (
    <>
      <Modal className="edit-cluster-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={handleModalClose}>
        <ModalHeader>Update cluster</ModalHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <fieldset className="edit-cluster-form__fieldset">
              <TextInput
                {...register('name')}
                error={errors.name?.message}
                label="Cluster name"
                placeholder="Enter a cluster name"
                required
              />
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <SelectInput
                    {...field}
                    {...reactSelectMenuPositionDef(MODAL_ID)}
                    error={errors.status?.message}
                    label="Status"
                    onChange={(value: unknown) => handleSelectFieldChange('status', value as IOption | null)}
                    options={statusOptions}
                    required
                    value={statusOptions.find(({ value }) => value === field.value)}
                  />
                )}
              />
              <TextArea
                {...register('description')}
                error={errors.description?.message}
                label="Description"
                placeholder="Enter a description"
              />
              <Controller
                control={control}
                name="userId"
                render={({ field }) => (
                  <SelectInput
                    {...field}
                    {...reactSelectMenuPositionDef(MODAL_ID)}
                    error={errors.userId?.message}
                    label="User assigned"
                    onChange={(value: unknown) => handleSelectFieldChange('userId', value as IOption | null)}
                    options={userOptions}
                    value={userOptions.find(({ value }) => value === field.value)}
                  />
                )}
              />
              <Controller
                control={control}
                name="tags"
                render={() => (
                  <>
                    <Label label="Tags" />
                    <ReactSelectCreatable
                      {...reactSelectMenuPositionDef(MODAL_ID)}
                      className="edit-cluster-tags-form__tags-input"
                      closeMenuOnSelect={false}
                      defaultValue={tags.map((el) => ({ label: el, value: el }))}
                      isMulti
                      name="tags"
                      onChange={(value: unknown) => handleSelectFieldChange('tags', value as IOption[])}
                      openMenuOnClick={clusterTags.length > 0}
                      options={options}
                      placeholder='Type a tag and press "Enter"'
                    />
                  </>
                )}
              />
              <TextInput
                error={errors.highVolumeThreshold?.message}
                label="High Volume Threshold"
                min={0}
                placeholder="Enter high volume threshold"
                required
                type="number"
                {...register('highVolumeThreshold')}
              />
            </fieldset>
            <hr className="edit-cluster-form__separator" />
            <fieldset className="edit-cluster-form__fieldset">
              <span className="edit-cluster-form__fieldset__legend">Advanced settings</span>
              <TextInput
                error={errors.nbLeaders?.message}
                label="Nb leaders"
                min={0}
                placeholder="Enter number of leaders"
                required
                type="number"
                {...register('nbLeaders')}
              />
              <div className="rhf-checkbox-input-wrapper">
                <Label id="enableKeywordScrapper" label="Enable keyword scrapper" />
                <input id="enableKeywordScrapper" type="checkbox" {...register('enableKeywordScrapper')} />
              </div>
              <div className="rhf-checkbox-input-wrapper">
                <Label id="enableReviewsScrapper" label="Enable reviews scrapper" />
                <input id="enableReviewsScrapper" type="checkbox" {...register('enableReviewsScrapper')} />
              </div>
            </fieldset>
          </ModalBody>
          <ModalFooter className="edit-cluster-modal__footer">
            <div className="edit-cluster-modal__footer__delete-actions-wrapper">
              <Button className="delete-button" onClick={handleDeleteModalState(true)}>
                Delete
              </Button>
            </div>
            <div className="edit-cluster-modal__footer__update-actions-wrapper">
              <Button ghost onClick={handleModalClose}>
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="submit">
                Update
              </Button>
            </div>
          </ModalFooter>
        </form>
        <DevTool {...methods} />
      </Modal>
      <Modal className="confirm-delete-modal" isOpen={isConfirmModalOpen} onCloseModal={handleDeleteModalState(false)}>
        <ModalHeader>Confirm deletion</ModalHeader>
        <ModalBody>
          <span>Are you sure you want to delete this cluster?</span>
        </ModalBody>
        <ModalFooter className="confirm-delete-modal__footer">
          <Button ghost onClick={handleDeleteModalState(false)}>
            Cancel
          </Button>
          <Button className="delete-button" disabled={isPending} onClick={handleDeleteConfirm}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default EditClusterModal
